import { getApiClient } from '../../../api';
import { Messages } from 'primereact/messages';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ProgressSpinner, Checkbox } from '../../../components/PrimereactComponents';
import { getErrorMessage } from '../../../utils/errors';
import { ButtonAdd } from '../../components/Buttons';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { Permission, UserRole } from '../../constants/enums';
import { hasPermission, hasRole } from '../../utils/auth';
import { showErrorMessage, showSuccessMessage } from '../../utils/messages';
import { AddRequestDialog } from './components/AddRequestDialog';
import { BodyDialog } from './components/BodyDialog';
import { ConfirmDeleteMunicipalityDialog } from './components/ConfirmDeleteMunicipalityDialog';
import { ConfirmDeleteBodyDialog } from './components/ConfirmDeleteBodyDialog';
import { MunicipalityTable } from './components/MunicipalityTable';
import { MunicipalityBreadCrumb } from '../../components/MunicipalityBreadCrumb';

export default function Municipalities() {
    const { municipalityId } = useParams();

    const { user } = useUserPermissions();

    const [municipalities, setMunicipalities] = useState(null);
    const [loading, setLoading] = useState(true);

    const [selectedMunicipality, setSelectedMunicipality] = useState(null);

    const [showAllMunicipalities, setShowAllMunicipalities] = useState(false);

    const [showDeleteMunicipalityDialog, setShowDeleteMunicipalityDialog] = useState(false);
    const [processingDeleteMunicipality, setProcessingDeleteMunicipality] = useState(false);

    const [showAddRequestDialog, setShowAddRequestDialog] = useState(false);
    const [processingAddRequest, setProcessingAddRequest] = useState(false);
    const refAddRequestDialogMsgs = useRef(null);
    const refAddRequestDialogForm = useRef(null);
    
    const [selectedBody, setSelectedBody] = useState(null);
    const [bodyTypeOptions, setBodyTypeOptions] = useState([]);
    
    const [showBodyDialog, setShowBodyDialog] = useState(false);
    const [processingBodyDialog, setProcessingBodyDialog] = useState(false);
    const refBodyDialogMsgs = useRef(null);
    const refBodyDialogForm = useRef(null);

    const [showDeleteBodyDialog, setShowDeleteBodyDialog] = useState(false);
    const [processingDeleteBody, setProcessingDeleteBody] = useState(false);

    const navigate = useNavigate();

    const msgs = useRef(null);

    useEffect(() => {
        const loadMunicipalities = async () => {
            const municipalitiesResponse = await getApiClient().getAdminMunicipality();
            setMunicipalities(municipalitiesResponse);
        };

        const loadBodyTypes = async () => {
            const bodyTypesResponse = await getApiClient().getAdminBodyType();
            setBodyTypeOptions(
                bodyTypesResponse.map((typOrganu) => ({
                    label: typOrganu.code,
                    value: typOrganu.id,
                    text_value: typOrganu.code
                }))
            );
        };

        const fetchData = async () => {
            try {
                await Promise.all([loadMunicipalities(), loadBodyTypes()]);
            } catch (error) {
                const errorMessage = getErrorMessage(error);
                showErrorMessage(msgs, errorMessage ?? 'Nepodařilo se načíst municipality');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Automatically show all municipalities when user doesn't manage any
    useEffect(() => {
        if (user?.permissions?.manage_municipality.length === 0) {
            setShowAllMunicipalities(true)
        }
    }, [user]);

    const confirmDeleteMunicipality = (rowData) => {
        setSelectedMunicipality(rowData);
        setShowDeleteMunicipalityDialog(true);
    };

    const hideDeleteMunicipalityDialog = () => {
        setShowDeleteMunicipalityDialog(false);
        setSelectedMunicipality(null);
    };

    const handleDeleteMunicipality = async () => {
        hideDeleteMunicipalityDialog();
        if (selectedMunicipality) {
            setProcessingDeleteMunicipality(true);
            try {
                await getApiClient().deleteAdminMunicipalityByMunicipalityId(selectedMunicipality.text_id);
                setMunicipalities(municipalities.filter((m) => m.text_id !== selectedMunicipality.text_id));
                showSuccessMessage(
                    msgs,
                    `Municipalita ${selectedMunicipality.name} (${selectedMunicipality.text_id}) byla úspěšně smazána`,
                    { replace: true }
                );
            } catch (error) {
                const errorMessage = getErrorMessage(error);
                showErrorMessage(msgs, errorMessage ?? 'Nepodařilo se smazat municipalitu', { replace: true });
            } finally {
                setProcessingDeleteMunicipality(false);
            }
        }
    };

    const handleClickAddRequest = (rowData) => {
        setSelectedMunicipality(rowData);
        setShowAddRequestDialog(true);
    };

    const hideAddRequestDialog = () => {
        setShowAddRequestDialog(false);
        setSelectedMunicipality(null);
    };

    const handleAddRequest = async (data) => {
        setProcessingAddRequest(true);
        const requestData = {
            municipality_text_id: selectedMunicipality.text_id,
            text: data.text
        };
        try {
            await getApiClient().postAdminManageMunicipality(requestData);
            showSuccessMessage(
                msgs,
                `Žádost o správu municipality ${selectedMunicipality.name} (${selectedMunicipality.text_id}) byla úspěšně podána`
            );
            hideAddRequestDialog();
            refAddRequestDialogForm?.current?.reset();
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(
                refAddRequestDialogMsgs,
                errorMessage ?? 'Nepodařilo se podat žádost o správu municipality',
                { replace: true }
            );
        } finally {
            setProcessingAddRequest(false);
        }
    };

    const handleClickAddBody = (rowData) => {
        setSelectedMunicipality(rowData);
        setShowBodyDialog(true);
    }

    const handleClickEditBody = (rowData) => {
        setSelectedBody(rowData);
        setShowBodyDialog(true);
    }

    const hideBodyDialog = () => {
        setShowBodyDialog(false);
        setSelectedMunicipality(null);
        setSelectedBody(null);
    }

    const handleBodyAddDialog = async (data) => {
        setProcessingBodyDialog(true);
        const requestData = {
            body_type: data.body_type,
            body_name: data.body_name,
            body_name_genitive: data.body_name_genitive,
            body_place: data.body_place,
            body_member_count: data.body_member_count,
            body_application_name: data.body_application_name,
            body_application_description: data.body_application_description
        }
        try {
            await getApiClient().postAdminBodyByMunicipalityId(selectedMunicipality.text_id, requestData);
            showSuccessMessage(
                msgs,
                `Orgán ${data.body_name} byl úspěšně vytvořen`
            );
            const selectedBodyType = bodyTypeOptions.find(option => option.value === data.body_type);
            navigate(`/admin/municipality/${selectedMunicipality.text_id}/${selectedBodyType.text_value}/funkcni-obdobi`)
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(
                refBodyDialogMsgs,
                errorMessage ?? 'Nepodařilo se vytvořit nový orgán',
                { replace: true }
            );
        } finally {
            setProcessingBodyDialog(false);
        }
    }

    const handleBodyEditDialog = async (data) => {
        setProcessingBodyDialog(true);
        const requestData = {
            body_name: data.body_name,
            body_name_genitive: data.body_name_genitive,
            body_place: data.body_place,
            body_member_count: data.body_member_count,
            body_application_name: data.body_application_name,
            body_application_description: data.body_application_description,
            body_data_url: data.body_data_url
        }
        try {
            await getApiClient().putAdminMunicipalityByMunicipalityIdAndBodyType(selectedBody.municipality_text_id, selectedBody.type_code, requestData);
            
            // Update table with edited information
            setMunicipalities((prevMunicipalities) =>
                prevMunicipalities.map((municipality) =>
                    municipality.text_id === selectedBody.municipality_text_id
                        ? {
                              ...municipality,
                              bodies: municipality.bodies.map((body) =>
                                  body.type_code === selectedBody.type_code
                                      ? {
                                            ...body,
                                            name: data.body_name,
                                            name_genitive: data.body_name_genitive,
                                            place: data.body_place,
                                            member_count: data.body_member_count,
                                            application_name: data.body_application_name,
                                            application_description: data.body_application_description,
                                            data_url: data.body_data_url
                                        }
                                      : body
                              )
                          }
                        : municipality
                )
            );

            hideBodyDialog();
            refBodyDialogForm?.current?.reset();
            showSuccessMessage(
                msgs,
                `Orgán ${data.body_name} byl úspěšně upraven`
            );
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(
                refBodyDialogMsgs,
                errorMessage ?? 'Nepodařilo se upravit orgán',
                { replace: true }
            );
        } finally {
            setProcessingBodyDialog(false);
        }
    }

    const confirmDeleteBody = (rowData) => {
        setSelectedBody(rowData);
        setShowDeleteBodyDialog(true);
    };

    const hideDeleteBodyDialog = () => {
        setShowDeleteBodyDialog(false);
        setSelectedBody(null);
    };

    const handleDeleteBody = async () => {
        hideDeleteBodyDialog();
        if (selectedBody) {
            setProcessingDeleteBody(true);
            try {
                await getApiClient().deleteAdminMunicipalityByMunicipalityIdAndBodyType(selectedBody.municipality_text_id, selectedBody.type_code);
                
                // Update table with edited information
                setMunicipalities((prevMunicipalities) =>
                    prevMunicipalities.map((municipality) =>
                        municipality.text_id === selectedBody.municipality_text_id
                            ? {
                                  ...municipality,
                                  bodies: municipality.bodies.filter((m) => m.type_code !== selectedBody.type_code)
                              }
                            : municipality
                    )
                );
                showSuccessMessage(
                    msgs,
                    `Orgán ${selectedBody.name} byl úspěšně smazán`,
                    { replace: true }
                );
            } catch (error) {
                const errorMessage = getErrorMessage(error);
                showErrorMessage(msgs, errorMessage ?? 'Nepodařilo se smazat orgán', { replace: true });
            } finally {
                setProcessingDeleteBody(false);
            }

        }
    }

    const handleCheckboxChange = () => {
        setShowAllMunicipalities(!showAllMunicipalities);
    }

    return (
        <>
            {municipalityId && <MunicipalityBreadCrumb />}
            <h1>Municipality</h1>

            <div className='page__buttons'>
                {hasRole(user, UserRole.ADMIN) && (
                    <ButtonAdd
                        label='Přidat municipalitu'
                        disabled={
                            processingDeleteMunicipality || processingAddRequest ||
                            processingBodyDialog || processingDeleteBody
                        }
                        onClick={() => navigate('/admin/municipality/new')}
                    />
                )}
                {hasRole(user, UserRole.USER) && (
                    <>
                        <ButtonAdd
                            label='Přidat municipalitu'
                            tooltip='Požádat o přidání nové municipality'
                            disabled={
                                processingDeleteMunicipality || processingAddRequest ||
                                processingBodyDialog || processingDeleteBody
                            }
                            onClick={() => navigate('/admin/nova-municipalita')}
                        />
                        <div className='show-all-checkbox-container'>
                            <label>
                                Zobrazit všechny municipality
                                <Checkbox
                                    name='municipalitiesCheckbox'
                                    checked={showAllMunicipalities}
                                    onChange={handleCheckboxChange}
                                />
                            </label>
                        </div>
                    </>
                )}
                {(processingDeleteMunicipality || processingAddRequest || processingBodyDialog || processingDeleteBody) && <ProgressSpinner />}
            </div>

            <Messages ref={msgs} className='page__messages' />

            <MunicipalityTable
                municipalities={municipalities ? 
                    (
                        showAllMunicipalities ?
                            municipalities : municipalities.filter(
                                municipality => hasPermission(user,
                                    Permission.MANAGE_MUNICIPALITY, municipality.text_id)
                                )
                    )
                    : null
                }
                loading={loading}
                user={user}
                onClickDeleteMunicipality={confirmDeleteMunicipality}
                onClickAddRequest={handleClickAddRequest}
                onClickAddBody={handleClickAddBody}
                onClickEditBody={handleClickEditBody}
                onClickDeleteBody={confirmDeleteBody}
                disabled={
                    processingDeleteMunicipality || processingAddRequest || 
                    processingBodyDialog || processingDeleteBody
                }
            />

            <ConfirmDeleteMunicipalityDialog
                isVisible={showDeleteMunicipalityDialog}
                onHide={hideDeleteMunicipalityDialog}
                handleDeleteMunicipality={handleDeleteMunicipality}
                municipality={selectedMunicipality}
            />

            <AddRequestDialog
                isVisible={showAddRequestDialog}
                onHide={hideAddRequestDialog}
                onSubmit={handleAddRequest}
                municipality={selectedMunicipality}
                msgs={refAddRequestDialogMsgs}
                ref={refAddRequestDialogForm}
            />
            
            <BodyDialog
                isVisible={showBodyDialog}
                onHide={hideBodyDialog}
                onSubmitAdd={handleBodyAddDialog}
                onSubmitEdit={handleBodyEditDialog}
                municipality={selectedMunicipality}
                editBody={selectedBody}
                bodyTypeOptions={bodyTypeOptions}
                msgs={refBodyDialogMsgs}
                ref={refBodyDialogForm}
            />

            <ConfirmDeleteBodyDialog
                isVisible={showDeleteBodyDialog}
                onHide={hideDeleteBodyDialog}
                handleDeleteBody={handleDeleteBody}
                body={selectedBody}
            />
            
        </>
    );
}
